import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import RecommendationTip from '../components/RecommendationTip';
import { useEntityConfig } from '../utils/EntityConfigContext';
import '../styles/ManagementControlScorecard.scss';
import { themeVars } from '../config/theme';
import { useSectorElementKey } from '../utils/useSectorElementKey';
import { DEMOGRAPHICS, DEMOGRAPHIC_LABELS, levelLabel, summariseHeadcount } from '../utils/headcount';

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);

const rowClass = (actual, target) => {
  if (actual === null || actual === 0) return 'sc-row--empty';
  return actual >= target ? 'sc-row--achieved' : 'sc-row--partial';
};

// Per-element display metadata. Adding a new sector's management-control
// variant means adding one entry here, not a new page file.
const SCORECARD_META = {
  management_control: {
    title: 'Management Control Scorecard',
    subtitle: 'B-BBEE — Management Control Element Calculation',
    backPath: '/management/management-control',
    docPath: '/management/submit-datasheet',
    docSheetName: 'Management Control Data Sheet',
    summaryLabel: 'Total Management Control Points',
    achievementLabel: 'Management Control Achievement',
    combineToggle: { settingsField: 'combine_exec_senior', label: 'Other Exec & Senior:' },
  },
  management_control_transport: {
    title: 'Management Control Scorecard (Transport)',
    subtitle: 'B-BBEE — Management Control Element Calculation (Transport Road Freight)',
    backPath: '/management/management-control',
    docPath: '/management/submit-datasheet/management-control-transport',
    docSheetName: 'Employment Equity Data Sheet Template Road Freight',
    summaryLabel: 'Total Management Control Points',
    achievementLabel: 'Management Control Achievement',
    combineToggle: { settingsField: 'combine_top_senior', label: 'Other Top & Senior Top:' },
  },
  employment_equity: {
    title: 'Employment Equity Scorecard',
    subtitle: 'B-BBEE — Employment Equity Element Calculation (Transport Road Freight)',
    backPath: '/management/employment-equity',
    docPath: '/management/submit-datasheet/employment-equity',
    docSheetName: 'Employment Equity Data Sheet Template Road Freight',
    summaryLabel: 'Total Employment Equity Points',
    achievementLabel: 'Employment Equity Achievement',
    combineToggle: { settingsField: 'combine_middle_junior', label: 'Middle & Junior:' },
  },
};

// The sector variants of this element; useSectorElementKey picks the one this company has.
const MC_VARIANTS = ['management_control', 'management_control_transport'];

const ManagementControlScorecardPage = () => {
  const navigate = useNavigate();
  const { elementKey: rawElementKey } = useParams();
  // Route segments are kebab-case (e.g. "employment-equity"); the backend's
  // element identifiers are snake_case (e.g. "employment_equity").
  const requestedKey = (rawElementKey || 'management_control').replace(/-/g, '_');
  const elementKey = useSectorElementKey(requestedKey, MC_VARIANTS); // null until resolved
  const meta = SCORECARD_META[elementKey || requestedKey] || SCORECARD_META.management_control;

  const { entity_size } = useEntityConfig();
  const isQse = entity_size === 'QSE';
  const [results, setResults] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [filename, setFilename] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [noDataSheet, setNoDataSheet] = useState(false);
  const [noEap, setNoEap] = useState(false);
  const [headcountSummary, setHeadcountSummary] = useState(null)
  const [CombinedPoints, setCombinedPoints] = useState(null);
  const [maxCombinedPoints, setMaxCombinedPoints] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [combineToggle, setCombineToggle] = useState(false);

  const applyData = (data) => {
    setResults(data.results);
    setTotalPoints(data.total_points);
    setFilename(data.filename);
    setIndicators(data.indicators || []);  //To pull indicators from backend instead of it being hardcoded in the frontend
    setNoDataSheet(false);
    setNoEap(false);
    setError(null);
    setHeadcountSummary(data.headcount_summary || null);
    setCombinedPoints(null);

  };

  const fetchCombinedTotal = () => {
    api.get('/core/scorecard/combined-total/')
      .then((res) => {
        setCombinedPoints(res.data.combined_points);
        if (res.data.max_points != null) setMaxCombinedPoints(res.data.max_points);
      })
      .catch(() => {});
  };

  const calculateFromDataSheet = async () => {
    setCalculating(true);
    setError(null);
    setNoDataSheet(false);
    setNoEap(false);
    try {
      const res = await api.post('/core/scorecard/calculate_from_data_sheet/', { element: elementKey });
      applyData(res.data);
      fetchCombinedTotal();
    } catch (err) {
      if (err.response?.status === 404) setNoDataSheet(true);
      else if (err.response?.status === 422) setNoEap(true);
      else setError(err.response?.data?.error || 'Failed to calculate from data sheet. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  useEffect (() => {
    if (!elementKey) return;
    api.get(`/core/scorecard/get_scorecard/?element=${elementKey}`)
    .then((res) => { applyData(res.data); setLoading(false); })
    .catch((err) => {
      setLoading(false);
      if (err.response?.data?.indicators?.length) setIndicators(err.response.data.indicators);
      if (err.response?.status === 404) calculateFromDataSheet();
    });
  }, [elementKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!elementKey || !meta.combineToggle) return;
    api.get(`/core/scorecard/element_settings/?element=${elementKey}`)
      .then(res => setCombineToggle(!!res.data[meta.combineToggle.settingsField]))
      .catch(() => {});
  }, [elementKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCombinedTotal(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resultMap = results
    ? Object.fromEntries(results.map((r) => [r.id, r]))
    : {};

  const maxPoints = indicators.reduce((s, ind) => s + (ind.points || 0), 0);

  let lastSection = null;

  const handleCombineToggle = async (value) => {
    if (!meta.combineToggle) return;
    setCombineToggle(value);
    await api.post('/core/scorecard/element_settings/', { element: elementKey, [meta.combineToggle.settingsField]: value });
    calculateFromDataSheet();
  };


  return (
    <div className="sc-container themed" style={themeVars(elementKey || requestedKey)}>
      <div className="sc-header">
        <button className="sc-back" onClick={() => navigate(meta.backPath)}>← Back</button>
        <div>
          <h1>{meta.title}</h1>
          <p>{meta.subtitle}</p>
        </div>
      </div>

      <div className="sc-upload-bar">
        <div className="sc-upload-info">
          {loading
            ? <span className="sc-upload-hint">Loading…</span>
            : filename
              ? <span className="sc-upload-name">✓ Calculated from: <strong>{filename}</strong></span>
              : <span className="sc-upload-hint">No data sheet found</span>
          }
        </div>
        <div className="sc-upload-actions">
          <button
            className="sc-template-btn"
            onClick={() => navigate(meta.docPath)}
          >
            Go to Documentation
          </button>
          <button
            className="sc-upload-btn"
            onClick={calculateFromDataSheet}
            disabled={calculating || loading}
          >
            {calculating ? 'Calculating…' : 'Recalculate'}
          </button>
        </div>
      </div>

      {!isQse && meta.combineToggle && (
        <div className="mc-combine-toggle">
          <span className="mc-combine-label">{meta.combineToggle.label}</span>
          <div className="od-toggle">
            <button
              className={`od-toggle-btn${!combineToggle ? ' od-toggle-btn--active' : ''}`}
              onClick={() => handleCombineToggle(false)}
            >
              Separate
            </button>
            <button
              className={`od-toggle-btn${combineToggle ? ' od-toggle-btn--active' : ''}`}
              onClick={() => handleCombineToggle(true)}
            >
              Combined
            </button>
          </div>
        </div>
      )}

      {noDataSheet && (
        <div className="sc-error">
          No {meta.docSheetName} uploaded yet. Go to{' '}
          <button className="sc-error-link" onClick={() => navigate(meta.docPath)}>
            Documentation
          </button>{' '}
          and upload the <strong>{meta.docSheetName}</strong> Excel file.
        </div>
      )}

      {noEap && (
        <div className="sc-error">
          No EAP found for the selected province. Please update your province in{' '}
          <button className="sc-error-link" onClick={() => navigate('/finance-statutory/company-information')}>
            Company Information
          </button>{' '}
          or contact your administrator to load the EAP targets.
        </div>
      )}

      {error && <div className="sc-error">{error}</div>}

{headcountSummary?.length > 0 && (() => {
  const { levels, totals, disabled } = summariseHeadcount(headcountSummary);
  return (
  <div className="sc-table-wrapper">
    <h2 className="sc-section-title"><strong>Employee headcount summary</strong></h2>
    <table className="sc-headcount-table">
      <thead>
        <tr>
          <th>Level</th>
          <th>Total</th>
          {DEMOGRAPHIC_LABELS.map((l) => <th key={l}>{l}</th>)}
        </tr>
      </thead>
      <tbody>
        {levels.map((row) => (
          <tr key={row.level}>
            <td>{levelLabel(row.level)}</td>
            <td><strong>{row.total ?? '—'}</strong></td>
            {DEMOGRAPHICS.map((demo) => (
              <td key={demo} className={row[demo] === 0 ? 'sc-shortfall' : undefined}>{row[demo] ?? '—'}</td>
            ))}
          </tr>
        ))}
        <tr className="sc-total-row">
          <td><strong>Total employees (excluding directors) </strong></td>
          <td><strong>{totals.total}</strong></td>
          {DEMOGRAPHICS.map((demo) => <td key={demo}><strong>{totals[demo]}</strong></td>)}
        </tr>
        {disabled && (
          <tr>
            <td>Of whom: {levelLabel('disabled').toLowerCase()}</td>
            <td><strong>{disabled.total}</strong></td>
            {DEMOGRAPHICS.map((demo) => <td key={demo}>{disabled[demo] ?? '—'}</td>)}
          </tr>
        )}
      </tbody>
    </table>
  </div>
  );
})()}

      <div className="sc-table-outer">
      <div className="sc-table-wrapper">
        <table className="sc-table">
          <thead>
            <tr>
              <th>Indicator</th>
              <th>Compliance Target</th>
              <th>Max Points</th>
              <th>Actual (%)</th>
              <th>Points Achieved</th>
            </tr>
          </thead>
          <tbody>
            {indicators.map((ind) => {
              const showHeader = ind.section && ind.section !== lastSection;
              lastSection = ind.section;

              const r = resultMap[ind.id];
              const actual = r?.actual ?? null;
              const pts = r?.points ?? 0;
              const isMax = pts >= ind.points;
              const subMinFailed = r?.sub_minimum_met === false;      // Sub-min check for Road Freight Board Participation

              return (
                <React.Fragment key={ind.id}>
                  {showHeader && (
                    <tr className="sc-row--section-header">
                      <td colSpan="5">{ind.section}</td>
                    </tr>
                  )}
                <tr className={subMinFailed ? 'sc-row--submin-fail' : rowClass(actual, ind.target)}>
                  <td>{ind.label}</td>
                  <td>{ind.targetLabel ?? `${ind.target}%`}</td>
                  <td>{ind.points}</td>
                  <td>{actual !== null ? `${fmt(actual)}%` : '—'}</td>
                  <td>
                    <span className={`sc-points${isMax ? ' sc-points--max' : pts === 0 ? ' sc-points--zero' : ''}`}>
                      {fmt(pts)}
                    </span>
                    <RecommendationTip
                    ind={ind}
                    actual={actual}
                    pts={pts}
                    spendRow={headcountSummary?.find(r => r.level === ind.id)}
                      />
                  </td>
                </tr>
                {r?.eap_breakdown?.length > 0 && (
                  <tr>
                    <td colSpan="5">
                      {r.eap_breakdown.map((lvl) => {
                        const entries = Object.entries({
                          ...lvl.all,
                          ...Object.fromEntries(
                            Object.entries(lvl.female).map(([k, v]) => [`${k} (F-adj)`, v])
                          ),
                        });
                        const met = entries.filter(([, t]) => t.met);
                        const notMet = entries.filter(([, t]) => !t.met);
                        return (
                          <div key={lvl.level} className="sc-eap-level">
                            <strong>{lvl.level} {lvl.met ? '✓' : '✗'}</strong>
                            <div className="sc-eap-cols">
                              <div className="sc-eap-col">
                                <div className="sc-eap-col-title">Met</div>
                                {met.map(([demo, t]) => (
                                  <div key={demo}>{demo}: {t.actual}% / {t.eap}%</div>
                                ))}
                                {met.length === 0 && <div>—</div>}
                              </div>
                              <div className="sc-eap-col">
                                <div className="sc-eap-col-title">Not met</div>
                                {notMet.map(([demo, t]) => (
                                  <div key={demo} className="sc-shortfall">{demo}: {t.actual}% / {t.eap}%</div>
                                ))}
                                {notMet.length === 0 && <div>—</div>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
            <tr className="sc-total-row">
              <td colSpan="3">Total</td>
              <td />
              <td>{fmt(totalPoints)} / {maxPoints || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {results?.some(r => r.sub_minimum_met === false) && (
        <div className="sc-submin-icon-wrap">
          <span className="sc-submin-icon-btn">⚠</span>
          <div className="sc-submin-popup">
            Sub-minimum not met — board participation indicator scores 0 points.
          </div>
        </div>
      )}
      </div>

      <div className="sc-summary">
        <div className="sc-summary-card sc-summary-card--total">
          <div className="sc-summary-score">{fmt(totalPoints)} <span>/ {maxPoints || '—'}</span></div>
          <div className="sc-summary-label">{meta.summaryLabel}</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{CombinedPoints !== null ? fmt(CombinedPoints) : '-'} <span>/ {maxCombinedPoints ?? '—'}</span></div>
          <div className="sc-summary-label">Points (incl. bonus)</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : '—'}<span>%</span></div>
          <div className="sc-summary-label">{meta.achievementLabel}</div>
        </div>
      </div>

    </div>
  );
};

export default ManagementControlScorecardPage;
